import React, { useCallback } from 'react';
import { DateUtils } from 'react-day-picker';
import { t } from '@lingui/macro';

import { CalendarDirection } from '@rover/components/src/datetime/DatePicker';
import { ServiceType } from '@rover/constants/src/translations/service.constants';
import { Box, Text } from '@rover/kibble/core';
import FrequencyToggle from '@rover/react-lib/src/pages/search/SearchPage/components/ColorfulPerfectMatchSection/FrequencyToggle';
import { DayTimeAvailability } from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates';
import OvernightDateRange from '@rover/react-lib/src/pages/search/SearchPage/components/Filters/Dates/OvernightDateRange';
import { shouldShowControls } from '@rover/react-lib/src/pages/search/SearchPage/utilities';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import RepeatServiceScheduler from '@rover/shared/js/search/components/RepeatServiceScheduler';
import type { SearchFiltersOptions } from '@rover/types/src/apiResponses/SearchOptionsResponse';
import type { AvailabilitySearchFilters } from '@rover/types/src/AvailabilitySearchFilters';
import type { DateRange } from '@rover/types/src/datetime/DateRange';
import type { Day } from '@rover/types/src/datetime/Day';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';

import { SpacesRequired } from '../Filters';
import { SearchDesktopSelectDates } from '../SimplifiedFiltersBar/analyticsEvents';

import { ColorfulPerfectMatchFieldInteraction } from './analyticsEvents';

type Props = {
  searchFilters: SearchFilters;
  language: string;
  serviceType: SearchFilters['serviceType'];
  serviceTypeFilterOptions: SearchFiltersOptions;
  onChange: (filters: Partial<SearchFilters>) => void;
};

const getDatePlaceholders = (service: ServiceType): { start: string; end: string } => {
  const isBoarding = service === ServiceType.OVERNIGHT_BOARDING;
  if (isBoarding) {
    return { start: t`Drop off`, end: t`Pick up` };
  }
  return { start: t`Start`, end: t`End` };
};

const DateInputs = ({
  searchFilters,
  language,
  serviceType,
  serviceTypeFilterOptions,
  onChange,
}: Props): JSX.Element => {
  const { frequency, startDate, endDate, selectedDays } = searchFilters;

  const isOvernightService =
    serviceType === ServiceType.OVERNIGHT_BOARDING ||
    serviceType === ServiceType.OVERNIGHT_TRAVELING;

  const handleFrequencyChange = useCallback(
    (serviceFrequency?: ServiceFrequencyEnum) => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'dates' }));
      const shouldResetDate = serviceFrequency === ServiceFrequencyEnum.REPEAT;
      const shouldResetDays = serviceFrequency === ServiceFrequencyEnum.ONE_TIME;
      onChange({
        frequency: serviceFrequency,
        ...(shouldResetDate && { endDate: undefined }),
        ...(shouldResetDays && { selectedDays: [] }),
      });
    },
    [onChange]
  );

  const datePlaceholders = getDatePlaceholders(serviceType);

  const handleDateRangeChange = useCallback(
    (dateRange: Partial<DateRange>) => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'dates' }));
      onChange({ startDate: dateRange.startDate, endDate: dateRange.endDate });
      emitAnalyticsEvent(new SearchDesktopSelectDates());
    },
    [onChange]
  );

  const handleRepeatStartDateChange = useCallback(
    (selectedStartDate?: Date | null) => {
      emitAnalyticsEvent(new ColorfulPerfectMatchFieldInteraction({ field: 'dates' }));
      const hasValidDates = endDate && selectedStartDate;
      const isStartAfterEnd = hasValidDates && DateUtils.isDayAfter(selectedStartDate, endDate);
      const updatedEndDate = hasValidDates && !isStartAfterEnd ? endDate : undefined;
      onChange({ startDate: selectedStartDate, endDate: updatedEndDate });
      emitAnalyticsEvent(new SearchDesktopSelectDates());
    },
    [endDate, onChange]
  );

  const handleSelectedDaysChange = (days: Day[]): void => {
    onChange({ selectedDays: days });
  };

  const handleAvailabilityChange = (availabilityFilters: AvailabilitySearchFilters) => {
    onChange(availabilityFilters);
  };

  const { morningAvailability, middayAvailability, eveningAvailability } = serviceTypeFilterOptions;

  const { showDaytimeAvailability, showSpacesRequired } = shouldShowControls({
    serviceTypeSlug: serviceType,
    serviceOptions: serviceTypeFilterOptions,
  });

  return (
    <Box>
      {!isOvernightService && (
        <>
          <Text size="200" fontWeight="semibold" mb="1x" display="block">
            {t`How often?`}
          </Text>
          <FrequencyToggle frequency={frequency} onChange={handleFrequencyChange} />
        </>
      )}

      <Text size="200" fontWeight="semibold" mb="1x" display="block">
        {frequency === ServiceFrequencyEnum.ONE_TIME ? t`When?` : t`Which days do you need?`}
      </Text>
      {isOvernightService || frequency === ServiceFrequencyEnum.ONE_TIME ? (
        <OvernightDateRange
          language={language}
          searchFilters={{ startDate, endDate }}
          onChange={handleDateRangeChange}
          startPlaceholder={datePlaceholders.start}
          endPlaceholder={datePlaceholders.end}
          calendarDirection={CalendarDirection.UP}
          label=""
        />
      ) : (
        <RepeatServiceScheduler
          language={language}
          onSelectedDaysChange={handleSelectedDaysChange}
          onStartDateChange={handleRepeatStartDateChange}
          repeatStartDateLabel={t`When?`}
          selectedDays={selectedDays || []}
          showLabel
          serviceName=""
          startDate={startDate}
          startPlaceholder={datePlaceholders.start}
          disableValidation
        />
      )}

      {showDaytimeAvailability && (
        <Box mt="4x">
          <DayTimeAvailability
            availabilityBuckets={{
              morning: morningAvailability?.label ?? null,
              midday: middayAvailability?.label ?? null,
              evening: eveningAvailability?.label ?? null,
            }}
            label={t`Which time(s) do you need?`}
            labelIsVisible
            onChange={handleAvailabilityChange}
            searchFilters={searchFilters}
          />
        </Box>
      )}

      {showSpacesRequired && (
        <Box mt="4x">
          <SpacesRequired
            searchFilters={searchFilters}
            onChange={(spacesRequired) => onChange({ spacesRequired })}
          />
        </Box>
      )}
    </Box>
  );
};

export default DateInputs;
